import React from 'react';
import { TrendingUp, Users, Award, Target } from 'lucide-react';

const ClassStatistics = ({ moyenneSemestre }) => {
  const getMentionColor = (mention) => {
    const colors = {
      'EXCELLENT': 'text-green-700 bg-green-100 border-green-200',
      'TRES_BIEN': 'text-blue-700 bg-blue-100 border-blue-200',
      'BIEN': 'text-indigo-700 bg-indigo-100 border-indigo-200',
      'ASSEZ_BIEN': 'text-yellow-700 bg-yellow-100 border-yellow-200',
      'PASSABLE': 'text-orange-700 bg-orange-100 border-orange-200',
      'INSUFFISANT': 'text-red-700 bg-red-100 border-red-200',
      'AJOURNE': 'text-red-800 bg-red-200 border-red-300'
    };
    return colors[mention] || 'text-gray-700 bg-gray-100 border-gray-200';
  };

  const getMentionLabel = (mention) => {
    const labels = {
      'EXCELLENT': 'Excellent',
      'TRES_BIEN': 'Très bien',
      'BIEN': 'Bien',
      'ASSEZ_BIEN': 'Assez bien',
      'PASSABLE': 'Passable',
      'INSUFFISANT': 'Insuffisant',
      'AJOURNE': 'Ajourné'
    };
    return labels[mention] || mention;
  };

  // Fonction pour formater les moyennes avec gestion des valeurs nulles
  const formatMoyenne = (moyenne) => {
    if (moyenne === null || moyenne === undefined || moyenne === 0) return 'N/A';
    return `${moyenne.toFixed(2)}/20`;
  };

  // Fonction pour formater le taux de réussite
  const formatTauxReussite = (taux) => {
    if (taux === null || taux === undefined || taux === 0) return 'N/A';
    return `${taux.toFixed(1)}%`;
  };

  return (
    <div className="class-statistics bg-gray-50 border-t border-gray-200 p-6">
      <div className="flex items-center mb-6">
        <TrendingUp className="h-6 w-6 text-[#202654] mr-2" />
        <h3 className="text-xl font-bold text-gray-900">
          Résultats et Statistiques
        </h3>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Résultats de l'étudiant */}
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Award className="h-5 w-5 mr-2 text-[#202654]" />
            Résultats de l'étudiant
          </h4>
          
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Moyenne générale :</span>
              <span className={`text-xl font-bold px-3 py-1 rounded-lg ${
                moyenneSemestre?.moyenne_generale >= 10 
                  ? 'text-green-700 bg-green-100' 
                  : 'text-red-700 bg-red-100'
              }`}>
                {moyenneSemestre?.moyenne_generale?.toFixed(2) || '0.00'}/20
              </span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Mention :</span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium border ${
                getMentionColor(moyenneSemestre?.mention_semestre)
              }`}>
                {getMentionLabel(moyenneSemestre?.mention_semestre)}
              </span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Rang dans la classe :</span>
              <span className="text-lg font-bold text-[#202654]">
                {moyenneSemestre?.rang_classe || 'N/A'}e / {moyenneSemestre?.effectif_classe || 'N/A'}
              </span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Modules validés :</span>
              <span className="font-semibold">
                {moyenneSemestre?.modules_valides || 0} / {moyenneSemestre?.nombre_modules || 0}
              </span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Semestre validé :</span>
              <span className={`px-2 py-1 rounded text-sm font-medium ${
                moyenneSemestre?.semestre_valide 
                  ? 'text-green-700 bg-green-100' 
                  : 'text-red-700 bg-red-100'
              }`}>
                {moyenneSemestre?.semestre_valide ? 'OUI' : 'NON'}
              </span>
            </div>
          </div>
        </div>

        {/* Statistiques de la classe */}
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Users className="h-5 w-5 mr-2 text-[#202654]" />
            Statistiques de la classe
          </h4>
          
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Moyenne de classe :</span>
              <span className="text-lg font-semibold text-gray-900">
                {moyenneSemestre?.moyenne_classe?.toFixed(2) || 'N/A'}/20
              </span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Plus forte moyenne :</span>
              <span className="text-lg font-semibold text-green-700">
                {formatMoyenne(moyenneSemestre?.plus_forte_moyenne)}
              </span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Plus faible moyenne :</span>
              <span className="text-lg font-semibold text-red-700">
                {formatMoyenne(moyenneSemestre?.plus_faible_moyenne)}
              </span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Taux de réussite :</span>
              <span className="text-lg font-semibold text-blue-700">
                {formatTauxReussite(moyenneSemestre?.taux_reussite)}
              </span>
            </div>
          </div>
        </div>

        {/* Informations complémentaires */}
        <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
          <h4 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
            <Target className="h-5 w-5 mr-2 text-[#202654]" />
            Informations complémentaires
          </h4>
          
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Total coefficients :</span>
              <span className="font-semibold text-[#202654]">
                {moyenneSemestre?.total_coefficients || 0}
              </span>
            </div>
            
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Nombre de modules :</span>
              <span className="font-semibold">
                {moyenneSemestre?.nombre_modules || 0}
              </span>
            </div>
            
            <div className="text-sm text-gray-600 bg-blue-50 p-3 rounded-lg mt-4">
              <p className="font-medium text-blue-800 mb-1">Information :</p>
              <p>
                Les moyennes sont calculées selon le système de coefficients. 
                Un module est validé si sa moyenne est supérieure ou égale à 10/20.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClassStatistics;
